import { createSlice } from '@reduxjs/toolkit';
import { VoiceAgentAPI } from 'api/endpoints';
import ApiServices from 'api/apexchat.api.services';
import {
  getErrorMessage,
  ToastTemplate,
  tryParseJson
} from 'services/utils/generic.methods';
import { toast } from 'react-toastify';
import storage from 'services/utils/storage';
import { generateDropDownOptions } from 'pages/portal/modal/CreateReport/helper';
import { Enums } from 'services/dropdown-enums';
import { getCompanyById } from 'store/slices/company/companySlice';
import config from 'config';

/** Absolute URL for Call History APIs (optional REACT_APP_CALL_HISTORY_APIURL override). */
const callHistoryEndpoint = path => {
  const base = `${config.callHistoryApiUrl || config.apiUrl}${
    config.apiVersion || ''
  }`.replace(/\/$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
};

/**
 * @name @createSlice
 * @description create redux store slice for ai voice agent
 **/
export const voiceAgentSlice = createSlice({
  name: 'voiceAgent',
  initialState: {
    agent: null,
    serverAgent: null,
    backgroundSounds: null,
    callConnectSettings: null,
    callLog: null,
    callTranscript: null
  },

  reducers: {
    setAgentToState: (state, action) => {
      state.agent = action.payload;
      state.serverAgent = action.payload;
    },
    setServerAgentToState: (state, action) => {
      state.serverAgent = action.payload;
    },
    updateAgentData: (state, action) => {
      state.agent = { ...state.agent, ...action.payload };
    },
    updateServerAgentData: (state, action) => {
      state.serverAgent = { ...state.serverAgent, ...action.payload };
    },
    setCallConnectToState: (state, action) => {
      state.callConnectSettings = action.payload;
    },
    setCallLogToState: (state, action) => {
      state.callLog = action.payload;
    },
    setCallTranscriptToState: (state, action) => {
      state.callTranscript = action.payload;
    },
    clearAgentState: state => {
      storage.deleteItem('selected-agent');
      storage.deleteItem('voiceai-path');
      storage.deleteItem('agent-sidebar');
      storage.deleteItem('agentCalendar-tab');
      state.agent = null;
      state.serverAgent = null;
    },
    clearCallHistoryStates: state => {
      storage.deleteItem('selected-callLog');
      state.callLog = null;
      state.callTranscript = null;
    },
    setBackgroundSounds: (state, action) => {
      state.backgroundSounds = action.payload;
    }
  }
});

export const {
  setAgentToState,
  clearAgentState,
  updateAgentData,
  updateServerAgentData,
  setBackgroundSounds,
  setServerAgentToState,
  setCallConnectToState,
  setCallLogToState,
  setCallTranscriptToState,
  clearCallHistoryStates
} = voiceAgentSlice.actions;

export const selectAgentInfo = state => {
  return state.voiceAgent.agent;
};

export const selectServerAgent = state => {
  return state.voiceAgent.serverAgent;
};

export const selectCallLog = state => {
  return state.voiceAgent.callLog;
};

export const selectCallTranscript = state => {
  return state.voiceAgent.callTranscript;
};

export const selectBackgroundSounds = state => {
  return state.voiceAgent.backgroundSounds;
};

export const selectCallConnect = state => {
  return state.voiceAgent.callConnectSettings;
};

export const createAgent = (companyId, payload) => {
  let agentAPI = VoiceAgentAPI.updateAgent(companyId);
  return ApiServices.post(agentAPI, payload);
};

export const deleteAgent = (companyId, id) => {
  let endpoint = VoiceAgentAPI.deleteAgentById(companyId, id);
  return ApiServices.delete(endpoint);
};

/**
 * List API often returns companyId: 0; the real id is on agentSettingsJSON.CompanyId.
 * 0 is falsy in JS, so callers must not use `if (!companyId)`.
 */
export const resolveCallLogCompanyId = rowOrLog => {
  if (!rowOrLog) return null;

  const top = Number(rowOrLog.companyId);
  if (Number.isFinite(top) && top > 0) return top;

  const settings =
    typeof rowOrLog.agentSettingsJSON === 'string'
      ? tryParseJson(rowOrLog.agentSettingsJSON)
      : rowOrLog.agentSettingsJSON;
  const fromSettings = Number(settings?.CompanyId ?? settings?.companyId);
  if (Number.isFinite(fromSettings) && fromSettings > 0) return fromSettings;

  return null;
};

export const fetchCallLogById = id => async (dispatch, getState) => {
  const state = getState();
  const existingcallLog = selectCallLog(state);

  const callLog = JSON.parse(storage.getItem('selected-callLog'));
  const companyId = resolveCallLogCompanyId(callLog);

  if (companyId == null) {
    return { success: false };
  }
  if (Number(callLog?.id) !== Number(id)) {
    return { success: false };
  }
  // if (existingcallLog?.id === id) {
  //   return { success: true };
  // }

  try {
    const res = await getCallDetailsById(companyId, id);

    if (res.data?.success && res.data?.data) {
      const result = res.data.data;
      const agentSettingsJSON = tryParseJson(result.agentSettingsJSON);
      const resolvedCompanyId =
        resolveCallLogCompanyId({
          ...result,
          agentSettingsJSON
        }) || companyId;
      dispatch(
        setCallLogToState({
          ...result,
          companyId: resolvedCompanyId,
          agentSettingsJSON
        })
      );
      // Keep storage in sync so refresh/re-open still has a valid companyId
      storage.setItem('selected-callLog', {
        companyId: resolvedCompanyId,
        id: result.id ?? id
      });

      // Also fetch transcript of this call
      if (result?.callStatus?.value === Enums.CallStatus.Completed) {
        try {
          const trs = await getTranscriptById(id);
          if (trs.data?.success && trs.data?.data) {
            dispatch(
              setCallTranscriptToState({
                ...trs.data?.data,
                extractedInfoJSON: tryParseJson(
                  trs.data?.data.extractedInfoJSON
                )
              })
            );
          }
        } catch (ex) {
          console.error('Error fetching call transcript');
        }
      }
      return { success: true };
    }

    toast.error("You don't have access to the specified call log");
    return { success: false };
  } catch (err) {
    toast.error(ToastTemplate.error(getErrorMessage(err)));
    return { success: false };
  }
};

export const fetchAgentById = id => async (dispatch, getState) => {
  const state = getState();
  const existingAgent = selectServerAgent(state);

  const agent = JSON.parse(storage.getItem('selected-agent'));

  if (!agent?.companyId) {
    return { success: false };
  }
  if (agent?.id !== id) {
    return { success: false };
  }
  if (existingAgent?.id === id) {
    await dispatch(ensureAgentCompanyKey());
    return { success: true };
  }

  try {
    const res = await getVoiceAgentById(agent?.companyId, id);

    if (res.data?.success && res.data?.data) {
      const result = {
        ...res.data.data,
        companyId: res.data.data.companyId || agent.companyId
      };
      dispatch(setAgentToState(result));
      dispatch(
        setCallConnectToState({
          summarizeCallAfterCallTransfer: result.summarizeCallAfterCallTransfer,
          callTransferType: result.callTransferType,
          escalationPhoneNumber: result.escalationPhoneNumber,
          callTransferLogic: result.callTransferLogic
        })
      );
      await dispatch(ensureAgentCompanyKey());
      return { success: true };
    }

    toast.error("You don't have access to the specified agent");
    return { success: false };
  } catch (err) {
    toast.error(ToastTemplate.error(getErrorMessage(err)));
    return { success: false };
  }
};

/** Resolve agent companyKey from companyId when API omits companyKey. */
export const ensureAgentCompanyKey = () => async (dispatch, getState) => {
  const agent = selectAgentInfo(getState());
  const selected = JSON.parse(storage.getItem('selected-agent')) || {};
  if (!agent) return '';

  const existingKey = agent.companyKey || agent.CompanyKey || '';
  if (existingKey) return existingKey;

  const companyId =
    agent.companyId || agent.CompanyId || selected.companyId || 0;
  if (!companyId) return '';

  try {
    const res = await getCompanyById(companyId);
    const key =
      res?.data?.data?.companyKey || res?.data?.data?.CompanyKey || '';
    if (key) {
      dispatch(updateAgentData({ companyKey: key, companyId }));
    }
    return key;
  } catch (err) {
    console.error('Failed to resolve agent companyKey', err);
    return '';
  }
};

export const fetchBackgroundSounds = () => async (dispatch, getState) => {
  const state = getState();
  const existing = selectBackgroundSounds(state);

  if (existing) {
    return;
  }

  try {
    const res = await getBackgroundVoices();

    if (res.data?.success && res.data?.data) {
      dispatch(
        setBackgroundSounds(
          generateDropDownOptions(
            res.data.data,
            {
              valueProp: 'id',
              labelProp: 'name'
            },
            true
          )
        )
      );
    }
  } catch (err) {
    toast.error(ToastTemplate.error(getErrorMessage(err)));
  }
};

export const getVoiceAgentById = (companyId, id) => {
  let agentAPI = VoiceAgentAPI.getAgentById(companyId, id);
  return ApiServices.get(agentAPI);
};

export const getAllAgent = (payload, id) => {
  let endPoint = VoiceAgentAPI.getAllAgentList(id);
  return ApiServices.getWithParams(endPoint, { params: payload });
};

export const getAllAgentCalls = (payload, id) => {
  let endPoint = callHistoryEndpoint(VoiceAgentAPI.getAllAgentCallList(id));
  return ApiServices.getWithParams(endPoint, { params: payload });
};

export const getVoiceAgentQuickStats = (companyId, payload) => {
  let endPoint = VoiceAgentAPI.getQuickStats(companyId);
  return ApiServices.post(endPoint, payload);
};

export const getTranscriptById = id => {
  let endPoint = callHistoryEndpoint(VoiceAgentAPI.getTranscript(id));
  return ApiServices.get(endPoint);
};

export const updateAgent = (companyId, payload) => {
  let agentAPI = VoiceAgentAPI.updateAgent(companyId);
  return ApiServices.put(agentAPI, payload);
};

export const getCallDetailsById = (companyId, id) => {
  let endPoint = callHistoryEndpoint(
    VoiceAgentAPI.getCallDetails(companyId, id)
  );
  return ApiServices.get(endPoint);
};

export const getAllVoiceData = payload => {
  let endPoint = VoiceAgentAPI.getAllVoice;
  return ApiServices.getWithParams(endPoint, { params: payload });
};

export const getBackgroundVoices = () => {
  let endPoint = VoiceAgentAPI.backgroundSounds;
  return ApiServices.get(endPoint);
};

export const buyPhoneNumber = payload => {
  let agentAPI = VoiceAgentAPI.buyNumber;
  return ApiServices.post(agentAPI, payload);
};

export const releasePhoneNumber = payload => {
  let agentAPI = VoiceAgentAPI.releaseNumber;
  return ApiServices.post(agentAPI, payload);
};

export const updateVoiceType = (payload, companyId) => {
  let endpoint = callHistoryEndpoint(
    VoiceAgentAPI.updateVoiceTypeByIds(companyId)
  );
  return ApiServices.put(endpoint, payload);
};

export default voiceAgentSlice.reducer;
